<template>
  <div class="course-selection">
    <!-- Physics Direction Section -->
    <div style="height: 10px;"></div>
    <div class="course-card" :style="cardStyle">
      <div class="section-header">
        <span class="title">{{direction}}</span>
        <van-image
          width="66"
          height="53"
          :src="shuyuanIcon"
          class="header-icon"
        />
      </div>
      
      <!-- Course Levels -->
      <div class="course-levels">
        <!-- Level 1 -->
        <template v-for="(item,index) in info" :key="index">
          <div class="level" :class="index%2 == 0 ? 'margin-left-6': 'margin-right-6'" :style="levelStyle">
          <div class="level-header">{{item.subTitle}} <span class="level-score">{{item.total}}分</span></div>
          <div class="subjects">
            <div class="subject-row">
              <div class="subject-item">语文 {{item.scores.chinese}}</div>
              <div class="subject-item">数学 {{item.scores.math}}</div>
            </div>
            <div class="subject-row">
              <div class="subject-item">英语 {{item.scores.english}}</div>
              <div class="subject-item">综合 {{item.scores.composite}}</div>
            </div>
          </div>
        </div>
        </template>
        
      
      </div>
    </div>

    <!-- 成绩诊断卡片 -->
    <div class="recommended-schools" :class="{ 'collapsed': isDiagnosisCollapsed }" :style="showPhoneForm ? 'margin-bottom: 78px;' : ''">
      <div class="section-header" style="justify-content: space-between;" @click="toggleDiagnosis">
        <div style="display: flex; align-items: center;">
          <van-image
            width="32"
            height="32"
            :src="courseItemIcon"
            class="header-icon"
          />
          <span class="title" style="margin-left: 12px; color: #323233">成绩诊断</span>
        </div>
        <van-icon :name="isDiagnosisCollapsed ? 'arrow-down' : 'arrow-up'" color="#323233" size="18" />
      </div>
      <div class="card-content" :class="{ 'hidden': isDiagnosisCollapsed }">
        <div class="schools-list">
          <div class="stream-container" ref="container">
            <div class="content-block">
              <div class="content-body markdown-content">
                <div v-if="isLoading" class="loading-container">
                  <van-loading type="spinner" color="#5a68ea" size="24px">分析中...</van-loading>
                </div>
                <div v-else-if="diagnosisError" class="error-container">
                  <van-icon name="warning-o" color="#ee0a24" size="36" />
                  <p class="error-message">{{ diagnosisError }}</p>
                  <van-button 
                    plain
                    size="small"
                    type="danger" 
                    @click="retryInitData"
                  >
                    重试
                  </van-button>
                </div>
                <div v-else class="markdown-body" v-html="renderedContent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="phone-form" :class="{ 'show': showPhoneForm }">
        <van-field
          v-model="phoneNumber"
          type="tel"
          maxlength="11"
          placeholder="请输入手机号码"
          :rules="[{ pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' }]"
        />
        <van-button 
          type="primary" 
          block 
          :loading="isSubmitting"
          :disabled="!isValidPhone"
          @click="handleSubmit"
          class="breath-button"
        >
          <span class="breath-text">立即获取推荐高校</span>
        </van-button>
      </div>
    </div>

    <!-- 推荐高校卡片 -->
    <div v-if="showSchoolCard" class="recommended-schools school-card" :class="{ 'collapsed': isSchoolCollapsed }">
      <div class="section-header" style="justify-content: space-between;" @click="toggleSchool">
        <div style="display: flex; align-items: center;">
          <van-image
            width="32"
            height="32"
            :src="courseItemIcon"
            class="header-icon"
          />
          <span class="title" style="margin-left: 12px; color: #323233">推荐高校</span>
        </div>
        <van-icon :name="isSchoolCollapsed ? 'arrow-down' : 'arrow-up'" color="#323233" size="18" />
      </div>
      <div class="card-content" :class="{ 'hidden': isSchoolCollapsed }">
        <div class="schools-list">
          <div class="stream-container" ref="schoolContainer">
            <div class="content-block">
              <div class="content-body markdown-content">
                <div v-if="isSchoolLoading" class="loading-container">
                  <van-loading type="spinner" color="#5a68ea" size="24px">获取推荐高校中...</van-loading>
                </div>
                <div v-else-if="schoolError" class="error-container">
                  <van-icon name="warning-o" color="#ee0a24" size="36" />
                  <p class="error-message">{{ schoolError }}</p>
                  <van-button 
                    plain
                    size="small"
                    type="danger" 
                    @click="retryInitSchoolData"
                  >
                    重试
                  </van-button>
                </div>
                <div v-else class="markdown-body" v-html="renderedSchoolContent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部咨询电话 -->
    <div class="contact-phone" v-if="phoneNum">
      <a :href="'tel:' + phoneNum" class="phone-link">
        <van-icon name="phone-o" color="#5a68ea" size="20" />
        <span>咨询电话：{{phoneNum}}</span>
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue'
import { Image as VanImage, Loading as VanLoading, Field as VanField, Button as VanButton, Icon as VanIcon } from 'vant'
import MarkdownIt from 'markdown-it'
import courseCardBg from '../../assets/course/course-card.png'
import courseItemBg from '../../assets/course/course-item.png'
import shuyuanIcon from '../../assets/course/shuyuan-icon.png'
import courseItemIcon from '../../assets/course/exam-icon.gif'
import { postAiSendPromptMsg, postAiSendMsg } from '@/api/report'
import { useRoute, useRouter } from "vue-router";
import { useScroll, useIntervalFn } from '@vueuse/core';
import { querySurveyAddDetail } from '@/api/user';

const route = useRoute();

const router = useRouter();

const info = ref([])

const schools = []

const scoreDiagnosis = ref([])

const currentSchoolIndex = ref(0)
const currentCharIndex = ref(0)
const isTyping = ref(true)

const direction = ref(null)

const orgCode = ref(null)

const phoneNum = ref(null)

const cardStyle = {
  backgroundImage: `url(${courseCardBg})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 100%'
}

const levelStyle = {
  backgroundImage: `url(${courseItemBg})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 100%'
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const isCharVisible = (schoolIndex, charIndex) => {
  if (schoolIndex < currentSchoolIndex.value) {
    return true
  }
  if (schoolIndex === currentSchoolIndex.value) {
    return charIndex <= currentCharIndex.value
  }
  return false
}

const typeText = async () => {
  for (let schoolIndex = 0; schoolIndex < schools.length; schoolIndex++) {
    currentSchoolIndex.value = schoolIndex
    const school = schools[schoolIndex]
    
    for (let charIndex = 0; charIndex < school.length; charIndex++) {
      currentCharIndex.value = charIndex
      await sleep(50)
    }
    
    if (schoolIndex < schools.length - 1) {
      await sleep(500)
      currentCharIndex.value = -1
    }
  }
  isTyping.value = false
}

const messages = ref([]);
const isStreaming = ref(false);
const container = ref(null);
const currentContent = ref('');
const { y } = useScroll(container);

// 打字速度控制 (字符/毫秒)
const typingSpeed = 30;

// 添加 loading 状态
const isLoading = ref(true);

// 添加手机号码输入相关
const phoneNumber = ref('');
const showPhoneForm = ref(false);
const isSubmitting = ref(false);

// 添加手机号码验证计算属性
const isValidPhone = computed(() => {
  return /^1[3-9]\d{9}$/.test(phoneNumber.value);
});

// 添加推荐高校相关的响应式变量
const showSchoolCard = ref(false);
const isSchoolLoading = ref(false);
const isSchoolStreaming = ref(false);
const schoolContent = ref('');
const schoolContainer = ref(null);

// 添加折叠状态控制
const isDiagnosisCollapsed = ref(false);
const isSchoolCollapsed = ref(false);

// 添加错误状态变量
const diagnosisError = ref('');
const schoolError = ref('');

// 添加折叠控制函数
const toggleDiagnosis = () => {
  isDiagnosisCollapsed.value = !isDiagnosisCollapsed.value;
};

const toggleSchool = () => {
  isSchoolCollapsed.value = !isSchoolCollapsed.value;
};

// 修改提交处理函数
const handleSubmit = async () => {
  if (!isValidPhone.value) return;
    const formData = {
      [route?.query?.directionKey]: [{
        answer: route?.query?.directionValue, 
        answerType: 'option'
      }],
      '手机号': [{
        answer: phoneNumber.value, 
        answerType: 'text'
      }]
    };
    info.value.forEach(exam => {
      const examId = exam.id.toString();
      const answerArray = exam.subjects.map(subject => ({
        answer: subject.value,
        answerType: 'text',
        score: exam.scores[subject.key]
      })).sort((a, b) => a.answer.localeCompare(b.answer));
      
      formData[examId] = answerArray;
    });
  try {
    isSubmitting.value = true;
    const params = {
        surveyId: route?.query?.surveyId,
        formData,
    };
    const res = await querySurveyAddDetail({
      ...params
    })
    if (res.code == '00') {
      showPhoneForm.value = false; // 关闭弹窗
      isDiagnosisCollapsed.value = true; // 折叠成绩诊断卡片
      showSchoolCard.value = true; // 显示推荐高校卡片
      await initSchoolData(); // 请求推荐高校数据
    } else {
      showToast('获取失败')
    }
  } catch (error) {
    console.error('提交失败:', error);
  } finally {
    isSubmitting.value = false;
  }
};

// 解析返回的数据
function parseAIResponse(response) {
  try {
    if (response?.data?.output?.choices?.[0]?.message?.content) {
      return response.data.output.choices[0].message.content;
    }
    return '';
  } catch (error) {
    console.error('Parse response error:', error);
    return '';
  }
}

// 打字机效果实现
async function typeContent(content) {
  const chars = content.split('');
  let displayContent = '';
  
  for (let i = 0; i < chars.length; i++) {
    displayContent += chars[i];
    currentContent.value = displayContent;
    
    await nextTick();
    await scrollToBottom();
    
    await new Promise(resolve => setTimeout(resolve, typingSpeed));
  }
  
  // 打字效果完成后，显示手机号码输入表单
  await nextTick();
  showPhoneForm.value = true;
}

// 修改滚动到底部的函数
async function scrollToBottom() {
  await nextTick();
  const contentContainer = container.value;
  if (contentContainer) {
    const scrollHeight = contentContainer.scrollHeight;
    contentContainer.scrollTo({
      top: scrollHeight,
      behavior: 'instant'  // 改为 instant 以避免滚动动画导致的延迟
    });
  }
}

// 添加重试功能
const retryInitData = async () => {
  diagnosisError.value = '';
  await initData();
};

const retryInitSchoolData = async () => {
  schoolError.value = '';
  await initSchoolData();
};

const initSchoolData = async () => {
  try {
    isSchoolLoading.value = true;
    isSchoolStreaming.value = true;
    schoolContent.value = '';
    schoolError.value = ''; // 重置错误状态
    
    // 重置内容容器的滚动位置
    if (schoolContainer.value) {
      schoolContainer.value.scrollTop = 0;
    }
    
    const _info = info.value;
    let _message = `${direction.value}，`;
    _info.forEach(item => {
      _message += `${item.title}，`
      item.subjects.forEach(subject => {
        _message += `${subject.label}${item.scores[subject.key]}分，`
      })
    })

    const result = await postAiSendMsg({
      message: _message,
      businessType: 'ScoreDiagnosis',
      code: '0002',
      orgCode: orgCode.value
    });

    isSchoolLoading.value = false;
    
    // 解析并处理返回的内容
    const content = parseAIResponse(result);
    if (content) {
      // 开始打字效果
      await typeSchoolContent(content);
    } else {
      // 如果没有内容，显示错误
      schoolError.value = '未能获取推荐高校数据，请稍后重试';
    }
    
    isSchoolStreaming.value = false;
  } catch (error) {
    console.error('Init school data error:', error);
    isSchoolLoading.value = false;
    isSchoolStreaming.value = false;
    schoolError.value = '网络异常，获取推荐高校失败，请稍后重试';
  }
}

const initData = async () => {
  try {
    isLoading.value = true;
    isStreaming.value = true;
    currentContent.value = '';
    diagnosisError.value = ''; // 重置错误状态
    
    // 重置内容容器的滚动位置
    if (container.value) {
      container.value.scrollTop = 0;
    }
    
    const _info = info.value;
    let _message = `${direction.value}，`;
    _info.forEach(item => {
      _message += `${item.title}，`
      item.subjects.forEach(subject => {
        _message += `${subject.label}${item.scores[subject.key]}分，`
      })
    })

    const result = await postAiSendMsg({
      message: _message,
      businessType: 'ScoreDiagnosis',
      code: '0001',
      orgCode: orgCode.value
    });

    isLoading.value = false;
    
    // 解析并处理返回的内容
    const content = parseAIResponse(result);
    if (content) {
      // 开始打字效果
      await typeContent(content);
    } else {
      // 如果没有内容，显示错误
      diagnosisError.value = '未能获取成绩诊断数据，请稍后重试';
    }
    
    isStreaming.value = false;
  } catch (error) {
    console.error('Init data error:', error);
    isLoading.value = false;
    isStreaming.value = false;
    diagnosisError.value = '网络异常，获取成绩诊断失败，请稍后重试';
  }
}

// 添加学校内容的打字效果函数
async function typeSchoolContent(content) {
  const chars = content.split('');
  let displayContent = '';
  
  for (let i = 0; i < chars.length; i++) {
    displayContent += chars[i];
    schoolContent.value = displayContent;
    
    await nextTick();
    await scrollSchoolToBottom();
    
    await new Promise(resolve => setTimeout(resolve, typingSpeed));
  }
}

// 添加学校内容的滚动函数
async function scrollSchoolToBottom() {
  await nextTick();
  const contentContainer = schoolContainer.value;
  if (contentContainer) {
    const scrollHeight = contentContainer.scrollHeight;
    contentContainer.scrollTo({
      top: scrollHeight,
      behavior: 'instant'
    });
  }
}

// 初始化 markdown-it，启用所有需要的特性
const md = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true,
  typographer: true,
  // 启用更全面的markdown特性支持
  quotes: '""\'\'',
  langPrefix: 'language-'
});

// 添加渲染后的内容计算属性，确保不转义markdown标记
const renderedContent = computed(() => {
  // 确保内容不被过度处理，保留markdown标记完整性
  const processedContent = currentContent.value
    .replace(/\n{3,}/g, '\n\n') // 只合并三个以上的换行
    .trim();
  
  // 应用markdown渲染
  return md.render(processedContent || '');
});

const renderedSchoolContent = computed(() => {
  // 对学校内容也应用同样的处理
  const processedContent = schoolContent.value
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  
  return md.render(processedContent || '');
});

onMounted(() => {
  direction.value = route?.query?.direction
  orgCode.value = route?.query?.orgCode
  phoneNum.value = route?.query?.phoneNum
  const _info = JSON.parse(route?.query?.info)
  const keyMap = {
    'first': '一诊',
    'second': '二诊'
  }
  info.value = _info.map(item => {
    const scores = item.scores
    const total = Object.values(scores)
                   .map(Number)
                   .reduce((sum, score) => sum + score, 0);
    return {
      ...item,
      total,
      subTitle: keyMap[item.key]
    }
  })
  initData()
})
</script>

<style scoped>
.course-selection {
  padding: 12px;
  background-color: #F2F2F2;
  min-height: 100vh;
  padding-bottom: 54px;
}

.course-card {
  position: relative;
  width: calc(100% - 38px);
  border-radius: 12px;
  padding: 8px 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  position: relative;
}

/* 修改伪元素选择器，仅应用于顶部蓝色卡片 */
.course-card .section-header::after {
  content: '';
  position: absolute;
  left: 0px;
  right: 58px;
  bottom: 22px;
  height: 1px;
  background: #fff;
}

.header-icon {
  width: 77px;
  height: 63px;
}

.section-header .title {
  font-size: 17px;
  color: #fff;
  font-family: PingFang SC;
}

.course-levels {
  display: flex;
  margin-top: 8px;
}

.level {
  flex: 1;
  padding: 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
}

.level-header {
  font-size: 16px;
  color: rgba(255, 255, 255, 1);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.level-score {
  background: rgba(255, 255, 255, 1);
  color: #5a68ea;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
}

.subjects {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.subject-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.subject-item {
  color: rgba(255, 255, 255, 1);
  font-size: 15px;
  flex: 1;
  font-weight: 400;
  letter-spacing: 1px;
  white-space: nowrap;
}

.recommended-schools {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin: 12px 0 0;
  width: calc(100% - 32px);
  margin-bottom: 12px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 320px);
}

.recommended-schools.collapsed {
  height: auto !important;
  min-height: 52px;
}

.card-content {
  transition: all 0.3s ease;
  opacity: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-content.hidden {
  height: 0 !important;
  opacity: 0;
  margin: 0;
  padding: 0;
}

.schools-list {
  padding: 0;
  flex: 1;
  overflow: hidden;
  position: relative;
  height: calc(100vh - 380px);
}

.stream-container {
  font-family: 'PingFang SC', sans-serif;
  line-height: 1.6;
  background: #fff;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.content-block {
  background: #fff;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.content-body {
  padding: 12px 0;
  font-size: 14px;
  color: #323233;
  line-height: 1.6;
  flex: 1;
}

.markdown-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.markdown-body {
  font-size: 15px;
  line-height: 1.5;
  color: #2c3e50;
  white-space: normal; /* 修改为normal，让文本正常换行 */
  word-break: break-word;
  padding: 0 12px;
  text-align: left;
}

/* 修复markdown标题样式 */
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) {
  margin: 0.8em 0 0.4em;
  font-weight: 600;
  color: #222;
  line-height: 1.3;
}

.markdown-body :deep(h1) {
  font-size: 1.5em;
}

.markdown-body :deep(h2) {
  font-size: 1.3em;
}

.markdown-body :deep(h3) {
  font-size: 1.2em;
}

.markdown-body :deep(h4) {
  font-size: 1.1em;
}

/* 修复粗体和强调样式 */
.markdown-body :deep(strong) {
  font-weight: 600;
  color: #5a68ea; /* 突出强调的蓝色 */
}

.markdown-body :deep(em) {
  font-style: italic;
}

/* 修复列表样式 */
.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 1.5em;
  margin: 0.3em 0;
}

.markdown-body :deep(li) {
  margin: 0.2em 0;
  padding: 0;
  position: relative;
}

.section-header {
  cursor: pointer;
  user-select: none;
  padding: 8px 0;
  display: flex;
  align-items: center;
}

.section-header .title {
  font-size: 17px;
  color: #fff;
  font-family: PingFang SC;
  font-weight: 500;
}

.school-card {
  margin-top: 12px;
}

.avatar-icon {
  border-radius: 50%;
  overflow: hidden;
}

.header-text {
  flex: 1;
}

.header-text .title {
  font-size: 15px;
  font-weight: 500;
  color: #fff;
  margin-bottom: 4px;
}

.header-text .desc {
  font-size: 13px;
  color: #969799;
}

.character {
  opacity: 0;
  animation: fadeIn 0.1s calc(var(--char-index) * 30ms) forwards;
}

.typing-cursor {
  display: inline-block;
  width: 2px;
  height: 14px;
  background: #5a68ea;
  animation: blink 1s infinite;
  vertical-align: middle;
  margin-left: 2px;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

@keyframes blink {
  50% { opacity: 0; }
}

.loading-container {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  min-height: 100px;
  width: 100%;
}

.phone-form {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: -200px; /* 初始位置在屏幕下方 */
  background: #fff;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
}

.phone-form.show {
  transform: translateY(-216px);
}

.phone-form :deep(.van-field) {
  margin-bottom: 12px;
  border-radius: 8px;
  background: #f5f5f5;
}

.phone-form :deep(.van-button) {
  height: 44px;
  border-radius: 8px;
  font-size: 16px;
  background: #5a68ea;
  border: none;
  overflow: hidden; /* 确保动画不会溢出按钮 */
}

/* 呼吸效果文本 */
.breath-text {
  display: inline-block;
  animation: breath-animation 3s infinite ease-in-out;
  transform-origin: center;
}

/* 呼吸动画效果 */
@keyframes breath-animation {
  0%, 100% {
    transform: scale(1);
    text-shadow: none;
  }
  50% {
    transform: scale(1.1);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
  }
}

/* 按钮光晕效果 */
.breath-button {
  position: relative;
  box-shadow: 0 0 15px rgba(90, 104, 234, 0.3);
  animation: button-glow 4s infinite ease-in-out;
}

@keyframes button-glow {
  0%, 100% {
    box-shadow: 0 0 15px rgba(90, 104, 234, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(90, 104, 234, 0.7);
  }
}

.margin-left-6 {
  margin-left: -6px;
}

.margin-right-6 {
  margin-left: 6px;
}

.error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 140px;
  width: 100%;
  padding: 20px 0;
}

.error-message {
  color: #ee0a24;
  margin: 12px 0;
  font-size: 16px;
  text-align: center;
}

.contact-phone {
  text-align: center;
}

.phone-link {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5a68ea;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
}

.phone-link span {
  margin-left: 8px;
}

</style> 